import type { ConnectedPosition, OverlayRef } from '@angular/cdk/overlay';
import type {
  ComponentRef,
  ElementRef,
  InjectionToken,
  Signal,
  Type,
  ViewContainerRef,
} from '@angular/core';
import { Service } from '@angular/core';
import type { Observable } from 'rxjs';
import { filter, takeUntil } from 'rxjs';
import { ONYX_DROPDOWN_VIEWPORT_MARGIN } from '../../components/dropdown/constants';
import type { OnyxOverlayConfig } from '../../directives/interfaces';
import { OnyxOverlayPosition } from '../../enums';
import { DropdownHelper } from '../../helpers/dropdown.helper';
import { OnyxOverlayService } from './onyx-overlay.service';

@Service()
export class OnyxDropdownOverlayService {
  private readonly overlayService = new OnyxOverlayService();

  public detach$: Observable<void>;
  public componentRef: Signal<ComponentRef<unknown> | null>;

  private intersectionObserver?: IntersectionObserver;

  constructor() {
    this.detach$ = this.overlayService.detach$;
    this.componentRef = this.overlayService.componentRef;
  }

  public attachDropdown<T extends OnyxOverlayConfig, CT>(
    sourceRef: ElementRef,
    viewContainerRef: ViewContainerRef,
    component: Type<CT>,
    injectionToken: InjectionToken<T>,
    config: T,
  ): void {
    this.detachDropdown();

    this.createOverlayWithPositions(sourceRef, config);
    this.createDropdownAndAttachToOverlay(
      viewContainerRef,
      component,
      injectionToken,
      this.resolveConfig(config, sourceRef),
    );

    this.intersectionObserver = this.hideOnNotIntersecting(
      sourceRef,
      this.overlayService,
    );
    this.detachDropdownOnClickOutside(sourceRef);
  }

  public detachDropdown(): void {
    this.overlayService.detachFromOverlay();
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = undefined;
  }

  private resolveConfig<T extends OnyxOverlayConfig>(
    config: T,
    sourceRef: ElementRef,
  ): T {
    if (!('maxHeight' in config) || config.maxHeight !== 'fill') return config;

    return {
      ...config,
      maxHeight: DropdownHelper.getViewportMaxHeight(
        config.elementRef ?? sourceRef.nativeElement,
        config.positions,
      ),
    };
  }

  private createOverlayWithPositions(
    sourceRef: ElementRef,
    config: OnyxOverlayConfig,
  ): void {
    const offsetY = config.targetRef
      ? sourceRef.nativeElement.getBoundingClientRect().top -
        config.targetRef.getBoundingClientRect().top
      : 0;
    const positions = this.getOverlayPositions(config, offsetY).map(
      (position) => {
        const panelClass = [
          ...(position.panelClass as string[]),
          ...(config.overridePositions?.panelClass ?? []),
          `width-${config.width}`,
          config.theme ?? 'light',
        ];
        return { ...position, ...config.overridePositions, panelClass };
      },
    );

    const positionStrategy = this.overlayService.getDefaultPositionStrategy(
      config.targetRef ?? sourceRef.nativeElement,
      positions,
      ONYX_DROPDOWN_VIEWPORT_MARGIN,
    );
    this.overlayService.createOverlay(sourceRef, {
      positionStrategy,
      scrollStrategy: this.overlayService.scrollStrategies.reposition({
        scrollThrottle: 0,
      }),
      hasBackdrop: false,
    });
  }

  private createDropdownAndAttachToOverlay<T>(
    viewContainerRef: ViewContainerRef,
    component: Type<any>,
    injectionToken: InjectionToken<T>,
    config: T,
  ): void {
    const dropdownInjector = this.overlayService.createComponentInjector(
      injectionToken,
      config,
    );
    const dropdownPortal = this.overlayService.createComponentPortal(
      component,
      dropdownInjector,
      viewContainerRef,
    );
    this.overlayService.attachToOverlay(dropdownPortal);
  }

  private hideOnNotIntersecting(
    elementRef: ElementRef,
    target: OnyxOverlayService | OverlayRef,
  ): IntersectionObserver {
    const intersectionObserver = new IntersectionObserver(([element]) => {
      if (element.isIntersecting) {
        target.removePanelClass('hidden');
      } else {
        target.addPanelClass('hidden');
      }
    });
    intersectionObserver.observe(elementRef.nativeElement);

    return intersectionObserver;
  }

  private detachDropdownOnClickOutside(sourceRef: ElementRef): void {
    this.overlayService.outsidePointerEvents$
      ?.pipe(
        filter((event) => !sourceRef.nativeElement.contains(event.target)),
        takeUntil(this.overlayService.detach$),
      )
      .subscribe(() => this.detachDropdown());
  }

  private getOverlayPositions(
    config: OnyxOverlayConfig,
    offsetY = 0,
  ): ConnectedPosition[] {
    let positions = config.positions;
    if (!positions?.length) {
      positions = [OnyxOverlayPosition.BOTTOM, OnyxOverlayPosition.TOP];
    }

    return positions
      .flatMap(
        (position) =>
          ({
            [OnyxOverlayPosition.TOP]: (['start', 'end'] as const).map(
              (x) =>
                ({
                  ...OnyxOverlayService.positionStrategyTop,
                  originX: x,
                  overlayX: x,
                  panelClass: ['top'],
                }) satisfies ConnectedPosition,
            ),
            [OnyxOverlayPosition.RIGHT]: [
              {
                ...OnyxOverlayService.positionStrategyRight,
                panelClass: ['right'],
              } satisfies ConnectedPosition,
            ],
            [OnyxOverlayPosition.BOTTOM]: (['start', 'end'] as const).map(
              (x) =>
                ({
                  ...OnyxOverlayService.positionStrategyBottom,
                  originX: x,
                  overlayX: x,
                  panelClass: ['bottom'],
                }) satisfies ConnectedPosition,
            ),
            [OnyxOverlayPosition.LEFT]: [
              {
                ...OnyxOverlayService.positionStrategyLeft,
                panelClass: ['left'],
              } satisfies ConnectedPosition,
            ],
          })[position],
      )
      .map((position) => ({
        ...position,
        ...(config.targetRef && {
          originY: 'top',
          overlayY: 'top',
          offsetY,
        }),
      }));
  }
}
